// Demo client for sales calls.
//
// Staff pick "Demo client" in the View as Client switcher and every page of
// the client portal renders a made-up client. Nothing reaches the server:
// api/clients/default.js request() swaps in demoAdapter while the demo is on
// (and the Guru chat stream short-circuits), so reads come from ./fixtures and writes
// are swallowed with a "demo only" toast. The session's own identity
// (accounts/auth/*) still goes to the server, so the staff login stays real.
import { enqueueSnackbar } from 'notistack';

import { matchDemoRoute } from './fixtures';
import { DEMO_CLIENT } from './fixtures/client';

const PREVIEW_STORAGE_KEY = 'viewAsClient';

function readPreview() {
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isDemoActive() {
  if (typeof window === 'undefined') return false;
  const preview = readPreview();
  return Boolean(preview && preview.demo);
}

// The value the switcher stores. The extra membership flags turn on the nav
// items a fully set-up client sees. Threshold billing (the Billing group)
// stays off: its every-$10k charges would contradict the bi-weekly invoices.
// Creative Performance (beta) stays off too: it has no demo fixtures.
export const DEMO_PREVIEW = {
  id: DEMO_CLIENT.id,
  name: DEMO_CLIENT.name,
  slug: DEMO_CLIENT.slug,
  demo: true,
  ad_performance_enabled: false,
  membership: {
    client_status: 'active',
    budget_center_enabled: true,
    client_billing_ui_enabled: false,
    ad_performance_enabled: false,
    invoices_outstanding_count: 0,
  },
};

// The staff login itself is never faked: token checks, login/logout and
// reading the signed-in user. Everything else under accounts/auth (team,
// profile edits, password) is answered by the demo.
const PASSTHROUGH = [
  /^accounts\/auth\/token\//,
  /^accounts\/auth\/(login|logout)\/?$/,
];
function isPassthrough(path, method) {
  if (method === 'GET' && /^accounts\/auth\/user\/?$/.test(path)) return true;
  return PASSTHROUGH.some((re) => re.test(path));
}

function normalizePath(config) {
  let url = String(config.url || '');
  const base = String(config.baseURL || '');
  if (base && url.startsWith(base)) url = url.slice(base.length);
  // Absolute URLs (paginated `next` links) — keep only the part after /api/.
  const apiIdx = url.indexOf('/api/');
  if (/^https?:\/\//.test(url) && apiIdx >= 0) url = url.slice(apiIdx + 5);
  const [path, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  Object.entries(config.params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.set(k, String(v));
  });
  return { path: path.replace(/^\/+/, ''), params };
}

function parseBody(data) {
  if (!data || typeof data !== 'string') return data || null;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

let lastToast = 0;
function toastWriteBlocked() {
  const now = Date.now();
  if (now - lastToast < 1500) return;
  lastToast = now;
  enqueueSnackbar('Demo client — changes are not saved.', { variant: 'info' });
}

// Empty fallback for routes without a fixture: an array that also carries the
// paginated keys, so both `data.map` and `data.results` render "no rows".
function emptyFallback() {
  return Object.assign([], { results: [], count: 0, next: null, previous: null });
}

export function shouldUseDemoAdapter(url, method = 'GET') {
  if (!isDemoActive()) return false;
  const { path } = normalizePath({ url });
  return !isPassthrough(path, String(method).toUpperCase());
}

// Pages that cache API rows in sessionStorage; cleared whenever the demo is
// switched on or off so real and demo rows never mix on screen.
const CACHED_KEYS = [/^leadListTableState/, /^clientDashboardLeadState/, /^client_zip_map_coverage/];
export function clearPageCaches() {
  try {
    Object.keys(window.sessionStorage)
      .filter((k) => CACHED_KEYS.some((re) => re.test(k)))
      .forEach((k) => window.sessionStorage.removeItem(k));
  } catch {
    /* storage disabled */
  }
}

export async function demoAdapter(config) {
  const method = String(config.method || 'get').toUpperCase();
  const { path, params } = normalizePath(config);
  const body = parseBody(config.data);

  const route = matchDemoRoute(method, path);
  let status = 200;
  let data;

  if (route) {
    data = await route.handler({ path, params, body, match: route.match, method });
    if (data && data.__status) {
      status = data.__status;
      data = data.body;
    }
  } else if (method === 'GET') {
    // eslint-disable-next-line no-console
    console.info('[demo] no fixture for', path, Object.fromEntries(params));
    data = emptyFallback();
  } else {
    data = { detail: 'Demo client — not saved.' };
  }

  if (method !== 'GET' && !(route && route.silent)) toastWriteBlocked();

  // A small delay so loading states look like a real portal, not a flash.
  await new Promise((r) => setTimeout(r, 120 + Math.random() * 180));

  return {
    data,
    status,
    statusText: 'OK',
    headers: { 'content-type': 'application/json' },
    config,
    request: {},
  };
}

// Ask Guru streams over raw fetch, which the adapter never sees. In demo it
// answers with a short canned reply so the chat still looks alive.
export async function streamDemoChat(payload, onEvent) {
  const reply = 'This is the demo account, so I can only give a sample answer here. '
    + 'For a live client I can pull up spend, leads and cost per appointment by market or vertical, '
    + 'explain pacing, and draft budget or ZIP changes for your team to approve.';
  onEvent({ type: 'session', session_id: payload?.session || 'demo-session' });
  const words = reply.split(' ');
  for (let i = 0; i < words.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 25));
    onEvent({ type: 'text_delta', text: `${i ? ' ' : ''}${words[i]}` });
  }
  onEvent({ type: 'done', content: reply, tool_calls: [], suggestions: [] });
}
